/* eslint-disable no-mixed-spaces-and-tabs */
import customers from '@/api/customers';
import Navigation from '@/components/Navigation/Navigation';
import ButtonBackHeader from '@/components/ui/Button/ButtonBackHeader';
import ParentSubpageShell from '@/components/Parent/ParentSubpageShell';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Tab, Tabs } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { route } from '@/constants/routeName';
import UserHeader from '@/components/User/UserHeader';
import NewTopUps from './NewTopUps';
import TabPanel from '@/components/ui/Tab/TabPanel';
import OnSiteTopUps from './OnSiteTopUps';
import Loader from '@/components/ui/Loader/Loader';
import { a11yProps } from '@/helper/a11yProps';

const { getTopUpsCustomer, getTopUpOrders } = customers();

const tabItems = [
	{ name: 'Self Service Top Up', index: 0 },
	{ name: 'On-site Top Up', index: 1 },
];

const pageSize = 20;
export default function TopUps() {
	const navigate = useNavigate();
	const innerHeight = useSelector((state) => state.content.innerHeight);
	const selectedCustomer = useSelector((state) => state.auth.selectedCustomer);
	const accountId = useSelector((state) => state.auth.user?.id);
	const accountType = useSelector((state) => state.auth.accountType);
	const isParent = accountType === 'parent';
	const customerId = selectedCustomer?.id ?? null;
	const studentName = selectedCustomer?.member?.name;

	const [page, setPage] = useState(1);
	const [pageN, setPageN] = useState(1);
	const [activeTab, setActiveTab] = useState(0);

	useEffect(() => {
		setPage(1);
		setPageN(1);
		setActiveTab(0);
	}, [customerId]);

	const objParam = useMemo(() => {
		const obj = { customer: customerId, type__in: ['Cash out', 'Deposit'], ordering: '-id', page_size: pageSize };
		if (page > 1) {
			obj.page = page;
		}
		return obj;
	}, [customerId, page]);

	const objParamN = useMemo(() => {
		const obj = { customer: customerId, ordering: '-id', page_size: pageSize };
		if (pageN > 1) {
			obj.page = pageN;
		}
		return obj;
	}, [customerId, pageN]);

	const {
		data: d,
		isFetching,
		isFetched,
	} = useQuery({
		queryKey: ['my-topup-orders', accountId, customerId, objParam],
		enabled: !!selectedCustomer && customerId != null,
		queryFn: () => getTopUpsCustomer(objParam),
		refetchOnWindowFocus: false,
	});

	const {
		data: dN,
		isFetching: isFetchingN,
		isFetched: isFetchedN,
	} = useQuery({
		queryKey: ['my-new-topup-orders', accountId, customerId, objParamN],
		enabled: !!selectedCustomer && customerId != null,
		queryFn: () => getTopUpOrders(objParamN),
		refetchOnWindowFocus: false,
	});

	const topUpList = useMemo(() => {
		let obj = d?.data?.results
			? d.data.results.reduce((group, product) => {
					const { date } = product;
					const dateKey = dayjs(date).format('YYYY-MM-DD');
					group[dateKey] = group[dateKey] ?? [];
					group[dateKey].push(product);
					return group;
			  }, {})
			: [];
		if (typeof obj === 'object' && Object.keys(obj).length) {
			obj = Object.keys(obj)
				.map((o) => {
					return { date: o, topUps: obj[o] };
				})
				.sort((a, b) => (dayjs(a.date).isAfter(dayjs(b.date)) ? -1 : 1));
		}
		return obj || [];
	}, [d]);
	const topUpCount = useMemo(() => {
		return d?.data?.count ? Math.floor(d?.data?.count / pageSize) : 0;
	}, [d]);

	const topUpListN = useMemo(() => {
		let obj = dN?.data?.results
			? dN.data.results.reduce((group, product) => {
					const date = product.requested_at;
					const dateKey = dayjs(date).format('YYYY-MM-DD');
					group[dateKey] = group[dateKey] ?? [];
					group[dateKey].push(product);
					return group;
			  }, {})
			: [];
		if (typeof obj === 'object' && Object.keys(obj).length) {
			obj = Object.keys(obj)
				.map((o) => {
					return { date: o, topUps: obj[o] };
				})
				.sort((a, b) => (dayjs(a.date).isAfter(dayjs(b.date)) ? -1 : 1));
		}
		return obj || [];
	}, [dN]);
	const topUpCountN = useMemo(() => {
		return dN?.data?.count ? Math.floor(dN?.data?.count / pageSize) : 0;
	}, [dN]);

	const handleChangePagination = (_, value) => {
		setPage(value);
	};

	const handleChangePaginationN = (_, value) => {
		setPageN(value);
	};

	const onTabChange = (_, newValue) => {
		setActiveTab(newValue);
	};

	useEffect(() => {
		if (topUpList?.length && !topUpListN?.length && activeTab === 0) {
			setActiveTab(1);
		}
	}, [topUpList, topUpListN, activeTab]);

	const historyBody = (
		<>
			{(isFetching || isFetchingN) && <Loader />}
			{isFetched && isFetchedN && (
				<div className='relative flex flex-col min-h-24'>
					{isParent ? (
						<div className='parent-tabs' role='tablist' aria-label='Top-up history'>
							{tabItems.map((tb) => (
								<button
									key={tb.name}
									type='button'
									role='tab'
									aria-selected={activeTab === tb.index}
									className={activeTab === tb.index ? 'on' : undefined}
									onClick={() => setActiveTab(tb.index)}
								>
									{tb.name}
								</button>
							))}
						</div>
					) : (
						<Tabs value={activeTab} onChange={onTabChange} aria-label='top-up tabs' className='jt-tabs'>
							{tabItems.map((tb, index) => (
								<Tab key={tb.name} className='p-4' name={tb.name} label={tb.name} {...a11yProps('top-up', index)} />
							))}
						</Tabs>
					)}

					{topUpListN?.length > 0 && (
						<TabPanel value={activeTab} index={0} name='Self Service Top Up'>
							<NewTopUps
								isFetching={isFetchingN}
								isFetched={isFetchedN}
								topUpList={topUpListN}
								topUpCount={topUpCountN}
								page={pageN}
								handleChangePagination={handleChangePaginationN}
							/>
						</TabPanel>
					)}
					<TabPanel value={activeTab} index={1} name='On-site Top Up'>
						<OnSiteTopUps
							isFetching={isFetching}
							isFetched={isFetched}
							topUpList={topUpList}
							topUpCount={topUpCount}
							page={page}
							handleChangePagination={handleChangePagination}
						/>
					</TabPanel>
				</div>
			)}
		</>
	);

	if (isParent) {
		return (
			<ParentSubpageShell
				title='My top-up history'
				backTo={`/${route.account}`}
				backLabel='← Back to account'
				intro={
					studentName
						? `History for ${studentName}.`
						: 'Select a child from Account to view top-up history.'
				}
			>
				{historyBody}
			</ParentSubpageShell>
		);
	}

	return (
		<div style={{ minHeight: innerHeight }} className='app-container student-page'>
			<UserHeader />
			<div className='flex flex-col w-full h-full gap-4 p-4 pb-24'>
				{studentName && (
					<p className='text-sm text-[var(--weak)]' data-testid='topups-ordering-for'>
						History for <span className='font-bold text-black'>{studentName}</span>
					</p>
				)}
				<div className='flex flex-col w-full gap-12'>
					<div className='relative'>
						<ButtonBackHeader className='top-0' beforeNavigate={() => navigate(`/${route.account}`)} />
						<div className='relative flex items-center justify-center'>
							<span className='ml-4 font-bold'>My Top-Up History</span>
						</div>
					</div>
					{historyBody}
				</div>
			</div>
			<Navigation />
		</div>
	);
}
